import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import PrintContext from "@/common/components/PrintContext";
import { ShowHeader } from "@/common/components/ShowHeader";
import Spinner from "@/common/components/Spinner";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import usePrint from "@/common/hooks/usePrint";
import { PrinterIcon } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useBranchesEquipmentsConsumptionsTotalizer } from "../../hooks/useClientCompanyBranchesEquipmentsConsumptionsTotalizer";
import BranchesEquipmentsConsumptionsTotalizerPrint from "./components/BranchesEquipmentsConsumptionsTotalizerPrint";
import BranchesEquipmentsConsumptionsTotalizerTable from "./components/table/BranchesEquipmentsConsumptionsTotalizerTable";
import ResumeItem from "@/common/components/ResumeItem";

const BranchesCompanyTotalsEquipmentsConsumptionsTotalizer = () => {
  const isMac = useMemo(() => {
    return /Macintosh|MacIntel|MacPPC|Mac68K/.test(window.navigator.platform) ||
      /Mac OS X/.test(window.navigator.userAgent);
  }, []);

  const {
    filterParams,
    setFilterParams,
  } = useFilterParams();

  const { date, formattedDate } = useFilterDate(filterParams);

  const {
    data: branchesEquipmentsConsumptionsTotalizer,
    meta: branchesEquipmentsConsumptionsTotalizerMeta,
    isPending: branchesEquipmentsConsumptionsTotalizerIsPending,
    isSuccess: branchesEquipmentsConsumptionsTotalizerIsSuccess,
    error: branchesEquipmentsConsumptionsTotalizerError = {} as any,
    refetch: refetchBranchesEquipmentsConsumptionsTotalizer,
    dataUpdatedAt: branchesEquipmentsConsumptionsTotalizerUpdatedAt,
    fetchAllBranchEquipments,
  } = useBranchesEquipmentsConsumptionsTotalizer({
    filters: {
      page: filterParams?.page || 1,
      consumptionCreatedAtGteq: formattedDate.from,
      consumptionCreatedAtLteq: formattedDate.to,
    },
  });

  const {
    print,
    contextProps,
  } = usePrint({
    pageSize: 'A4',
    forceColors: true,
    backgroundColor: '#FFFFFF',
  });

  const handlePrint = useCallback(() => {
    print({
      content: (
        <BranchesEquipmentsConsumptionsTotalizerPrint
          data={branchesEquipmentsConsumptionsTotalizer}
          meta={branchesEquipmentsConsumptionsTotalizerMeta}
        />
      ),
    });
  }, [branchesEquipmentsConsumptionsTotalizerUpdatedAt]);

  const handlePrintByKeyboardShortcut = (event: KeyboardEvent) => {
    if (!isMac && event.ctrlKey && event.code === 'KeyP') {
      event.preventDefault();
      handlePrint();
    }

    if (isMac && event.metaKey && !event.ctrlKey && event.code === 'KeyP') {
      event.preventDefault();
      handlePrint();
    }
  };

  useEffect(() => {
    document.removeEventListener('keydown', handlePrintByKeyboardShortcut, true);
    document.addEventListener('keydown', handlePrintByKeyboardShortcut, true);
  }, [branchesEquipmentsConsumptionsTotalizerUpdatedAt]);

  return (
    <div className="inner-screen">
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/movements-totalizer`,
          searchLastRouteFromFallback: true,
        }}
        title="Totalizadores"
        subtitle="Consumo de equipamentos por filial"
        titleClassName="text-md"
        className="text-white-400"
        subtitleClassName="text-nowrap font-medium"
        buttons={branchesEquipmentsConsumptionsTotalizerIsSuccess && (
          <Button onClick={handlePrint} className="transition-colors">
            <PrinterIcon strokeWidth={1.5} size={20} />

            Imprimir
          </Button>
        )}
      />

      <ErrorMessageWrapper
        statusCode={branchesEquipmentsConsumptionsTotalizerError?.status}
        refetchFunction={refetchBranchesEquipmentsConsumptionsTotalizer}
        className="flex-1"
      >
        {branchesEquipmentsConsumptionsTotalizerIsPending ? (
          <div className="flex-1 flex items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-y-hidden overflow-x-auto gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DatePickerWithRange
                  date={date}
                  setDate={(date) => setFilterParams((params) => ({ ...params, date }))}
                  className="overflow-hidden rounded-xl"
                />
              </div>

              <div>
                <ResumeItem
                  className="text-right"
                  label="Valor total das filiais"
                  value={branchesEquipmentsConsumptionsTotalizer?.[0]?.branchesTotalAmount || '--'}
                />
              </div>
            </div>

            <BranchesEquipmentsConsumptionsTotalizerTable
              data={branchesEquipmentsConsumptionsTotalizer}
              meta={branchesEquipmentsConsumptionsTotalizerMeta}
              fetchAllBranchEquipments={fetchAllBranchEquipments}
            />
          </div>
        )
        }
      </ErrorMessageWrapper >

      <PrintContext {...contextProps} className="text-black-900 bg-white-800 px-1 font-sans" />
    </div >
  );
}

export default BranchesCompanyTotalsEquipmentsConsumptionsTotalizer;
